import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { startOfDay, subDays, format, startOfWeek, startOfMonth, parseISO, differenceInDays } from 'date-fns';

export interface ScanRecord {
  id: string;
  scanned_at: string;
  ip_address: string | null;
  user_agent: string | null;
  country: string | null;
  city: string | null;
  device_type: string | null;
  os: string | null;
  browser: string | null;
  referrer: string | null;
}

export interface AnalyticsData {
  totalScans: number;
  scansToday: number;
  scansThisWeek: number;
  scansThisMonth: number;
  lastScanAt: string | null;
  avgScansPerDay: number;
  uniqueIPs: number;
  percentageChange: number;
  leadCount: number;
  conversionRate: number;
  scans: ScanRecord[];
}

export interface TimeSeriesData {
  date: string;
  scans: number;
}

export interface GeoData {
  location: string;
  count: number;
  percentage: number;
}

export interface DeviceData {
  name: string;
  count: number;
  percentage: number;
}

export interface HourlyPattern {
  hour: number;
  day: number;
  count: number;
}

export interface ReferrerData {
  source: string;
  count: number;
  percentage: number;
}

// Country flag emoji mapping
const countryToFlag: Record<string, string> = {
  'United States': '🇺🇸',
  'United Kingdom': '🇬🇧',
  'Germany': '🇩🇪',
  'France': '🇫🇷',
  'Spain': '🇪🇸',
  'Italy': '🇮🇹',
  'Canada': '🇨🇦',
  'Australia': '🇦🇺',
  'Japan': '🇯🇵',
  'China': '🇨🇳',
  'India': '🇮🇳',
  'Brazil': '🇧🇷',
  'Mexico': '🇲🇽',
  'Netherlands': '🇳🇱',
  'Sweden': '🇸🇪',
  'Norway': '🇳🇴',
  'Denmark': '🇩🇰',
  'Finland': '🇫🇮',
  'Poland': '🇵🇱',
  'Portugal': '🇵🇹',
  'Russia': '🇷🇺',
  'South Korea': '🇰🇷',
  'Singapore': '🇸🇬',
  'Hong Kong': '🇭🇰',
  'Taiwan': '🇹🇼',
  'Thailand': '🇹🇭',
  'Vietnam': '🇻🇳',
  'Indonesia': '🇮🇩',
  'Malaysia': '🇲🇾',
  'Philippines': '🇵🇭',
  'New Zealand': '🇳🇿',
  'South Africa': '🇿🇦',
  'Argentina': '🇦🇷',
  'Chile': '🇨🇱',
  'Colombia': '🇨🇴',
  'Egypt': '🇪🇬',
  'Turkey': '🇹🇷',
  'Israel': '🇮🇱',
  'UAE': '🇦🇪',
  'Saudi Arabia': '🇸🇦',
  'Ireland': '🇮🇪',
  'Switzerland': '🇨🇭',
  'Austria': '🇦🇹',
  'Belgium': '🇧🇪',
  'Czech Republic': '🇨🇿',
  'Greece': '🇬🇷',
  'Hungary': '🇭🇺',
  'Romania': '🇷🇴',
  'Ukraine': '🇺🇦',
};

export function getCountryFlag(country: string): string {
  return countryToFlag[country] || '🌍';
}

export function useQRAnalytics(qrCodeId: string | null, dateRange: { from: Date; to: Date }) {
  const { user } = useAuthStore();

  // Fetch all scans and leads for this QR code
  const { data: { scans = [], leads = [] } = { scans: [], leads: [] }, isLoading } = useQuery({
    queryKey: ['qr-analytics', qrCodeId, dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: async () => {
      if (!qrCodeId || !user) return { scans: [], leads: [] };
      
      const scansRes = await supabase
        .from('qr_scans')
        .select('*')
        .eq('qr_code_id', qrCodeId)
        .gte('scanned_at', dateRange.from.toISOString())
        .lte('scanned_at', dateRange.to.toISOString())
        .order('scanned_at', { ascending: false });

      const leadsRes = await (supabase as any)
        .from('leads')
        .select('*')
        .eq('qr_code_id', qrCodeId)
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());
        
      if (scansRes.error) throw scansRes.error;
      if (leadsRes.error) throw leadsRes.error;

      return { 
        scans: scansRes.data as ScanRecord[],
        leads: (leadsRes.data || []) as any[]
      };
    },
    enabled: !!qrCodeId && !!user,
  });

  // Calculate analytics
  const analytics: AnalyticsData = (() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const weekStart = startOfWeek(now);
    const monthStart = startOfMonth(now);
    const previousWeekStart = subDays(weekStart, 7);

    const scansToday = scans.filter(s => new Date(s.scanned_at) >= todayStart).length;
    const scansThisWeek = scans.filter(s => new Date(s.scanned_at) >= weekStart).length;
    const scansThisMonth = scans.filter(s => new Date(s.scanned_at) >= monthStart).length;
    const scansPreviousWeek = scans.filter(s => {
      const scanDate = new Date(s.scanned_at);
      return scanDate >= previousWeekStart && scanDate < weekStart;
    }).length;

    const uniqueIPs = new Set(scans.filter(s => s.ip_address).map(s => s.ip_address)).size;
    
    const daysSinceFirstScan = scans.length > 0 
      ? Math.max(1, differenceInDays(now, parseISO(scans[scans.length - 1].scanned_at)))
      : 1;
    const avgScansPerDay = scans.length / daysSinceFirstScan;

    const percentageChange = scansPreviousWeek > 0 
      ? Math.round(((scansThisWeek - scansPreviousWeek) / scansPreviousWeek) * 100)
      : scansThisWeek > 0 ? 100 : 0;

    const leadCount = leads.length;
    const conversionRate = scans.length > 0 
      ? Math.round((leadCount / scans.length) * 100) 
      : 0;

    return {
      totalScans: scans.length,
      scansToday,
      scansThisWeek,
      scansThisMonth,
      lastScanAt: scans.length > 0 ? scans[0].scanned_at : null,
      avgScansPerDay: Math.round(avgScansPerDay * 10) / 10,
      uniqueIPs,
      percentageChange,
      leadCount,
      conversionRate,
      scans,
    };
  })();

  // Time series data
  const timeSeries: TimeSeriesData[] = (() => {
    const dateMap = new Map<string, number>();
    
    // Initialize all dates in range
    let currentDate = new Date(dateRange.from);
    while (currentDate <= dateRange.to) {
      dateMap.set(format(currentDate, 'yyyy-MM-dd'), 0);
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }
    
    // Count scans per day
    scans.forEach(scan => {
      const date = format(new Date(scan.scanned_at), 'yyyy-MM-dd');
      dateMap.set(date, (dateMap.get(date) || 0) + 1);
    });
    
    return Array.from(dateMap.entries())
      .map(([date, scans]) => ({ date, scans }))
      .sort((a, b) => a.date.localeCompare(b.date));
  })();

  // Geographic data
  const geoData = (() => {
    const countryMap = new Map<string, number>();
    const cityMap = new Map<string, number>();
    
    scans.forEach(scan => {
      if (scan.country) {
        countryMap.set(scan.country, (countryMap.get(scan.country) || 0) + 1);
      }
      if (scan.city && scan.country) {
        const cityKey = `${scan.city}, ${scan.country}`;
        cityMap.set(cityKey, (cityMap.get(cityKey) || 0) + 1);
      }
    });
    
    const total = scans.length || 1;
    
    const countries: GeoData[] = Array.from(countryMap.entries())
      .map(([location, count]) => ({
        location,
        count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
      
    const cities: GeoData[] = Array.from(cityMap.entries())
      .map(([location, count]) => ({
        location,
        count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    return { countries, cities };
  })();

  // Device breakdown
  const deviceData = (() => {
    const deviceMap = new Map<string, number>();
    const osMap = new Map<string, number>();
    const browserMap = new Map<string, number>();
    
    scans.forEach(scan => {
      const device = scan.device_type || 'Unknown';
      const os = scan.os || 'Unknown';
      const browser = scan.browser || 'Unknown';
      
      deviceMap.set(device, (deviceMap.get(device) || 0) + 1);
      osMap.set(os, (osMap.get(os) || 0) + 1);
      browserMap.set(browser, (browserMap.get(browser) || 0) + 1);
    });
    
    const total = scans.length || 1;
    
    const mapToArray = (map: Map<string, number>): DeviceData[] =>
      Array.from(map.entries())
        .map(([name, count]) => ({
          name,
          count,
          percentage: Math.round((count / total) * 100),
        }))
        .sort((a, b) => b.count - a.count);
    
    return {
      devices: mapToArray(deviceMap),
      operatingSystems: mapToArray(osMap),
      browsers: mapToArray(browserMap),
    };
  })();

  // Hourly patterns (hour x day of week heatmap)
  const hourlyPatterns: HourlyPattern[] = (() => {
    const patternMap = new Map<string, number>();
    
    // Initialize all combinations
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        patternMap.set(`${day}-${hour}`, 0);
      }
    }
    
    scans.forEach(scan => {
      const scanDate = new Date(scan.scanned_at);
      const day = scanDate.getDay();
      const hour = scanDate.getHours();
      const key = `${day}-${hour}`;
      patternMap.set(key, (patternMap.get(key) || 0) + 1);
    });
    
    return Array.from(patternMap.entries()).map(([key, count]) => {
      const [day, hour] = key.split('-').map(Number);
      return { day, hour, count };
    });
  })();

  // Best times
  const bestTimes = (() => {
    const dayTotals = new Map<number, number>();
    const hourTotals = new Map<number, number>();
    
    hourlyPatterns.forEach(({ day, hour, count }) => {
      dayTotals.set(day, (dayTotals.get(day) || 0) + count);
      hourTotals.set(hour, (hourTotals.get(hour) || 0) + count);
    });
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const byDay = Array.from(dayTotals.entries())
      .map(([day, count]) => ({ name: dayNames[day], count }))
      .sort((a, b) => dayNames.indexOf(a.name) - dayNames.indexOf(b.name));
      
    const byHour = Array.from(hourTotals.entries())
      .map(([hour, count]) => ({ 
        name: hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`,
        hour,
        count 
      }))
      .sort((a, b) => a.hour - b.hour);
    
    return { byDay, byHour };
  })();

  // Referrer sources
  const referrerData: ReferrerData[] = (() => {
    const referrerMap = new Map<string, number>();
    
    scans.forEach(scan => {
      let source = 'Direct';
      if (scan.referrer) {
        if (scan.referrer.includes('facebook') || scan.referrer.includes('fb.')) {
          source = 'Facebook';
        } else if (scan.referrer.includes('twitter') || scan.referrer.includes('t.co')) {
          source = 'Twitter/X';
        } else if (scan.referrer.includes('instagram')) {
          source = 'Instagram';
        } else if (scan.referrer.includes('linkedin')) {
          source = 'LinkedIn';
        } else if (scan.referrer.includes('tiktok')) {
          source = 'TikTok';
        } else if (scan.referrer.includes('google')) {
          source = 'Google';
        } else if (scan.referrer.includes('mail') || scan.referrer.includes('outlook')) {
          source = 'Email';
        } else {
          source = 'Website';
        }
      }
      referrerMap.set(source, (referrerMap.get(source) || 0) + 1);
    });
    
    const total = scans.length || 1;
    
    return Array.from(referrerMap.entries())
      .map(([source, count]) => ({
        source,
        count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.count - a.count);
  })();

  return {
    analytics,
    timeSeries,
    geoData,
    deviceData,
    hourlyPatterns,
    bestTimes,
    referrerData,
    isLoading,
    scans,
  };
}

// Real-time subscription hook
export function useRealtimeScans(qrCodeId: string | null, onNewScan: (scan: ScanRecord) => void) {
  const { user } = useAuthStore();

  useQuery({
    queryKey: ['realtime-scans', qrCodeId],
    queryFn: async () => {
      if (!qrCodeId || !user) return null;

      const channel = supabase
        .channel(`scans-${qrCodeId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'qr_scans',
            filter: `qr_code_id=eq.${qrCodeId}`,
          },
          (payload) => {
            onNewScan(payload.new as ScanRecord);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    },
    enabled: !!qrCodeId && !!user,
    staleTime: Infinity,
  });
}
